import { useState, useRef, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, User } from "lucide-react";
import type { Invoice } from "@/types";

interface InsightsChatPanelProps {
  captureRate: number;
  missedSavings: number;
  totalSaved: number;
  availableBudget: number;
  allInvoices?: Invoice[];
}

interface Message {
  role: "user" | "ai";
  text: string;
}

function fmt(n: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

function pct(n: number) {
  return `${n.toFixed(1)}%`;
}

const PROMPTS = [
  "What's driving my low capture rate?",
  "Which suppliers should I prioritise?",
  "How much more could I save this quarter?",
  "Show me this month's summary",
] as const;

export function InsightsChatPanel({
  captureRate,
  missedSavings,
  totalSaved,
  availableBudget,
  allInvoices = [],
}: InsightsChatPanelProps) {
  const declinedInvoices = allInvoices.filter((i) => i.status === "standard_payment");
  const declinedCount = declinedInvoices.length;
  const declinedSavings = declinedInvoices.reduce((s, i) => s + (i.discount_amount ?? 0), 0);

  // Top 3 suppliers by missed discount
  const bySupplier = declinedInvoices.reduce<Record<string, number>>((acc, i) => {
    acc[i.supplier_name] = (acc[i.supplier_name] ?? 0) + (i.discount_amount ?? 0);
    return acc;
  }, {});
  const topMissed = Object.entries(bySupplier)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  function getResponse(prompt: string): string {
    const p = prompt.toLowerCase();

    if (p.includes("capture rate") || p.includes("driving")) {
      const declinePct =
        declinedCount > 0 ? ((declinedCount / (allInvoices.length || 1)) * 100).toFixed(0) : "0";
      return `Your capture rate is ${pct(captureRate)}. The biggest drag is ${declinedCount} supplier${declinedCount !== 1 ? "s" : ""} who declined offers — that's ${fmt(declinedSavings)} in missed discounts. Consider adjusting your APR offer or following up directly. Budget constraints are blocking an additional ${fmt(missedSavings - declinedSavings > 0 ? missedSavings - declinedSavings : 0)} in potential savings. Together these account for roughly ${declinePct}% of your eligible invoice volume.`;
    }

    if (p.includes("supplier") || p.includes("prioriti")) {
      if (topMissed.length === 0) {
        return `No declined invoices found in the current dataset — your supplier acceptance rate looks strong! Focus on expanding offers to the ${fmt(availableBudget)} of unused budget capacity.`;
      }
      const list = topMissed
        .map(([name, amt], i) => `${i + 1}. ${name} — ${fmt(amt)} in missed discount`)
        .join("\n");
      return `The suppliers with the most uncaptured value are:\n\n${list}\n\nI'd recommend a direct outreach to these ${topMissed.length} suppliers — even a 1% APR adjustment could unlock significant savings.`;
    }

    if (p.includes("save") || p.includes("quarter") || p.includes("more")) {
      const projection = Math.round((totalSaved / (captureRate / 100)) * 0.95);
      const upside = projection - totalSaved;
      return `At your current ${pct(captureRate)} capture rate, you've locked in ${fmt(totalSaved)} this period. If you close the ${fmt(missedSavings)} gap — by resolving supplier declines and deploying your ${fmt(availableBudget)} remaining budget — you could add ~${fmt(upside > 0 ? upside : missedSavings)} more this quarter. That puts your total capture potential around ${fmt(projection > totalSaved ? projection : totalSaved + missedSavings)}.`;
    }

    if (p.includes("summary") || p.includes("month")) {
      return `Here's your payment terms snapshot:\n\n• Captured: ${fmt(totalSaved)} (${pct(captureRate)} capture rate)\n• Still on the table: ${fmt(missedSavings)}\n• Budget available: ${fmt(availableBudget)}\n• Declined offers: ${declinedCount} invoice${declinedCount !== 1 ? "s" : ""} (${fmt(declinedSavings)} missed)\n\nYour program is performing well — the main opportunity is re-engaging the ${declinedCount} suppliers who said no.`;
    }

    return `Based on your current data: you're capturing ${pct(captureRate)} of eligible invoices with ${fmt(totalSaved)} locked in. There's ${fmt(missedSavings)} still available. Can you be more specific? Try asking about your capture rate, top suppliers, or quarterly savings potential.`;
  }

  // Pre-seeded conversation shown on load
  const initialMessages: Message[] = [
    {
      role: "user",
      text: "How many savings opportunities am I acting on?",
    },
    {
      role: "ai",
      text: `You're capturing ${pct(captureRate)} of eligible invoices — ${fmt(totalSaved)} locked in so far. There's still ${fmt(missedSavings)} on the table this period that hasn't been captured yet.`,
    },
    {
      role: "user",
      text: "What's blocking me from capturing more?",
    },
    {
      role: "ai",
      text:
        declinedCount > 0
          ? `Two things. First, ${declinedCount} supplier${declinedCount !== 1 ? "s" : ""} declined your early payment offers — that's ${fmt(declinedSavings)} in missed discounts. Second, your available budget of ${fmt(availableBudget)} may be limiting how many offers you can send. Increase budget or re-engage declined suppliers to unlock more.`
          : `Your available budget of ${fmt(availableBudget)} is the main constraint. Increasing it would allow DiscoRun to send more offers and capture more of the ${fmt(missedSavings)} still on the table.`,
    },
  ];

  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [input, setInput] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = (text: string) => {
    if (!text.trim()) return;
    const userMsg: Message = { role: "user", text: text.trim() };
    const aiMsg: Message = { role: "ai", text: getResponse(text.trim()) };
    setMessages((prev) => [...prev, userMsg, aiMsg]);
    setInput("");
  };

  return (
    <Card className="glass-card overflow-hidden border-primary/20">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-primary/8 to-transparent border-b border-primary/10">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold text-foreground">Ask DiscoRun AI</span>
          <Badge
            variant="outline"
            className="text-[10px] text-primary border-primary/30 px-1.5 py-0"
          >
            DiscoRun AI
          </Badge>
        </div>
        <p className="text-[11px] text-muted-foreground hidden sm:block">
          Powered by your live invoice data
        </p>
      </div>

      {/* Messages */}
      <div className="px-4 py-3 space-y-3 max-h-72 overflow-y-auto bg-primary/[0.02]">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex items-start gap-2.5 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
          >
            {/* Avatar */}
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                msg.role === "ai" ? "bg-primary/15" : "bg-muted"
              }`}
            >
              {msg.role === "ai" ? (
                <Sparkles className="w-3 h-3 text-primary" />
              ) : (
                <User className="w-3 h-3 text-muted-foreground" />
              )}
            </div>

            {/* Bubble */}
            <div
              className={`max-w-[80%] px-3 py-2 rounded-xl text-xs leading-relaxed whitespace-pre-line ${
                msg.role === "ai"
                  ? "bg-muted/60 text-foreground rounded-tl-none"
                  : "bg-primary text-primary-foreground rounded-tr-none"
              }`}
            >
              {msg.text}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Suggested prompt chips */}
      <div className="px-4 pt-2 pb-1 flex flex-wrap gap-1.5 border-t border-border/40">
        {PROMPTS.map((p) => (
          <button
            key={p}
            onClick={() => send(p)}
            className="text-[11px] px-2.5 py-1 rounded-full bg-primary/8 text-primary border border-primary/20 hover:bg-primary/15 transition-colors"
          >
            {p}
          </button>
        ))}
      </div>

      {/* Input */}
      <div className="px-4 pb-4 pt-2 flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send(input)}
          placeholder="Ask a question about your payment terms..."
          className="h-8 text-xs bg-muted/40"
        />
        <Button size="sm" className="h-8 px-3 flex-shrink-0" onClick={() => send(input)}>
          <Send className="w-3.5 h-3.5" />
        </Button>
      </div>
    </Card>
  );
}
